// apps/client/src/hooks/pipelineTracker/useSalesAnalyticsReport.ts
//
// Loads deal mix, pipeline velocity and its trend, the forecast and planned settlements for the
// Pipeline tab (SalesTrekker V6, phase 3). Same scope as usePipelineReport, and the same window:
// a rolling period by key, or an applied custom pair of dates.
//
// A SECOND CALL rather than more fields on pipelineReport, deliberately. These sections read the
// Sales export beside the Performance one and add three warehouse reads; folding them into the
// report every other section waits on would make the whole tab as slow as its slowest section.
// The tab renders as soon as the pipeline report lands and these sections fill in after.

import { useMemo } from 'react';
import { REPORT_CALLABLE } from './reportContract';
import { useTrackerCall } from './reportCache';
import {
  DEFAULT_MIX_STAGE, SALES_ANALYTICS_REPORT_ACTION, type MixStage, type SalesAnalyticsResponse,
} from './salesAnalyticsTypes';

export type RollingPeriod = 'wtd' | 'mtd' | 'qtd' | 'fytd' | 'cytd';

export interface UseSalesAnalyticsParams {
  companyId: string;
  asAtDate?: string;
  /** Rolling window. Ignored when a custom window is supplied. */
  period?: RollingPeriod;
  windowStart?: string;
  windowEnd?: string;
  adviserId?: string;
  groupKey?: string;
  workflowName?: string;
  dealLabel?: string;
  /**
   * Which Deal movements flow the deal mix wheels cut. Settlements is the server's default and is
   * never sent, so the default request (and its cache key) is the request from before the choice.
   */
  mixStage?: MixStage;
  /**
   * One referrer's deals only: a `PartnerRow.key` from Leads & Partners. Never sent when absent,
   * so every request without a referrer is the request from before the filter existed.
   */
  partnerKey?: string;
  /**
   * False while the operator has chosen Custom but not yet applied dates: there is no window to
   * ask about, and asking with the rolling default would print figures for dates nobody chose.
   */
  enabled?: boolean;
}

export interface UseSalesAnalyticsReturn {
  response: SalesAnalyticsResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSalesAnalyticsReport({
  companyId, asAtDate, period, windowStart, windowEnd,
  adviserId, groupKey, workflowName, dealLabel, mixStage, partnerKey, enabled = true,
}: UseSalesAnalyticsParams): UseSalesAnalyticsReturn {
  // Absent fields are OMITTED, never sent as undefined: the transport turns undefined into null
  // and the server's optional fields refuse null.
  const request = useMemo(() => (companyId && enabled ? {
    action: SALES_ANALYTICS_REPORT_ACTION,
    companyId,
    ...(asAtDate ? { asAtDate } : {}),
    ...(windowStart && windowEnd ? { windowStart, windowEnd } : period ? { period } : {}),
    ...(adviserId ? { adviserId } : {}),
    ...(groupKey && !adviserId ? { groupKey } : {}),
    ...(workflowName ? { workflowName } : {}),
    ...(dealLabel ? { dealLabel } : {}),
    ...(mixStage && mixStage !== DEFAULT_MIX_STAGE ? { mixStage } : {}),
    ...(partnerKey ? { partnerKey } : {}),
  } : null), [companyId, asAtDate, period, windowStart, windowEnd, adviserId, groupKey, workflowName, dealLabel, mixStage, partnerKey, enabled]);
  const { data, dataKey, key, loading, error, refetch } = useTrackerCall<SalesAnalyticsResponse>(
    REPORT_CALLABLE, request, {
      label: 'useSalesAnalyticsReport',
      // Surfaced, never swallowed: an empty wheel reads as "nothing settled", which is a claim.
      describeError: (err) => (err instanceof Error ? err.message : 'Failed to load the sales analytics'),
    });
  // NEVER A PREVIOUS WINDOW'S FIGURES. The response is returned only while the request it answered
  // is still the request on screen. Before this, switching to Custom (which disables the hook
  // until dates are applied) left the last period's Deal mix, Velocity, Forecast and Planned on
  // screen with nothing to say they were for other dates; and any change of window showed the old
  // figures until the new ones landed.
  const response = request && dataKey === key && !error ? data : null;
  return { response, loading: request !== null && loading, error, refetch };
}
